import {
  SubscriptionFlowDefinition,
  type FlowAnswers,
  type FlowMetadataInput,
  type PriceTag,
  type RecapPolicy,
} from './SubscriptionFlowDefinition'
import type { AvailableBundlesParams, Bundle } from '@/services/subscription'

/**
 * Arriving-in-France journey (`/arrivee-en-france`, subcategory PF).
 *
 * The decision tree consults only the length of stay (DecisionTree::PackFrance):
 * up to 3 months → Pack Global, 3 to 8 months → Pack Confort. The `long` case
 * (> 8 months) never reaches a flow — it redirects to the health journey at
 * render time. The tree already singles out one pack, so the results screen
 * shows only the backend-recommended bundle.
 *
 * Like the other duration-based covers, the subscription period is the chosen
 * start date over `duration` months and the price is the standard
 * monthly_price × months, so neither `period()` nor `displayPrice()` is
 * overridden. The cover is the stay itself (a fixed period), so it does not
 * renew tacitly.
 */

/** Front `franceStay` answer → backend `travel_duration` param. */
const FRANCE_STAY_TO_DURATION: Record<string, AvailableBundlesParams['travelDuration']> = {
  short: 'less_than_3',
  mid: 'between_3_and_8',
}

export class ArrivingFranceFlow extends SubscriptionFlowDefinition {
  readonly product = 'arrivingFrance' as const
  readonly subcategory = 'PF' as const

  /** The cover is the stay itself (a fixed period), so it does not renew. */
  readonly tacitRenewal = false

  /** The decision tree already picks the pack (Global vs Confort), so the
   *  results screen shows only the backend-recommended bundle. */
  readonly recommendedOnly = true

  /** Fixed-period stay cover (no tacit renewal), paid upfront or monthly. */
  readonly recap: RecapPolicy = {
    showPaymentSelector: true,
    showClub: false,
    tacitRow: 'no',
    priceMode: 'duration',
  }

  /** Source of truth: DecisionTree::PackFrance on the backend. */
  readonly planReference: Record<string, number> = {
    packFranceGlobal: 33,
    packFranceConfort: 4,
  }

  bundlesParams(a: FlowAnswers): AvailableBundlesParams | null {
    if (!a.franceStay) return null
    const travelDuration = FRANCE_STAY_TO_DURATION[a.franceStay]
    if (!travelDuration) return null
    return { subcategory: 'PF', travelDuration }
  }

  /** PF packs are monthly-priced and carry no annual_price; the yearly figure is
   *  the monthly rate × 12 (for display only — the real total comes from
   *  compute_price over the chosen duration). */
  displayPrice(bundle: Bundle): PriceTag {
    const monthly = bundle.monthlyPrice
    return { perMonth: monthly, perYear: monthly != null ? monthly * 12 : null }
  }

  /** The contract carries the ISO code of the subscriber's nationality. */
  contractMetadata(_answers: FlowAnswers, input: FlowMetadataInput): Record<string, string> | null {
    if (!input.nationality) return null
    return { nationality: input.nationality }
  }
}

